import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import { usePermissions } from '../context/PermissionContext';
import StatusBadge from '../components/StatusBadge';

// Admin/accounting expense hub (Slices A1 + A3): Queue (all reports,
// server-side filters + pagination), Summary (monthly aggregates), Limits
// (per-employee monthly limit admin). Decision controls live on
// ExpenseDetail (A2); composition stays on the personal Expenses page.

const STATUS_FILTERS = ['all', 'draft', 'submitted', 'approved', 'rejected', 'paid', 'voided'];
const PAGE_SIZE = 50;
// P2 (queue search): the four typed filters. Typed values debounce into
// `applied` (~400 ms, or Enter for an immediate apply); only `applied` reaches
// the query string, trimmed and only when non-blank — the list AND the XLSX
// export read the same object so the document mirrors the tab.
const SEARCH_KEYS = ['title', 'reference', 'amount_min', 'amount_max'];
const EMPTY_SEARCH = { title: '', reference: '', amount_min: '', amount_max: '' };
const SEARCH_DEBOUNCE_MS = 400;
const sameSearch = (a, b) => SEARCH_KEYS.every((k) => a[k].trim() === b[k].trim());
const appendSearchParams = (params, search) => {
  for (const k of SEARCH_KEYS) {
    const v = search[k].trim();
    if (v) params.set(k, v);
  }
};
// Mirrors the backend AMOUNT_RE (limits accept 0 — deliberate hard-warning signal).
const LIMIT_AMOUNT_RE = /^\d{1,8}(\.\d{1,2})?$/;

const formatAmount = (value) => `$${Number(value || 0).toFixed(2)}`;

// Client-LOCAL current month (D6): the server's month default is the UTC
// clock and can be off-by-one near month boundaries — we always send an
// explicit ?month= computed from the viewer's calendar.
const localMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// Export download (Slice A4a) — the Accidents.js blob idiom, with one
// improvement per the A4 design: error responses arrive as blobs too, so the
// catch decodes the blob back to JSON to surface the server's message.
const downloadExport = async (path, filename) => {
  try {
    const response = await api.get(path, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  } catch (err) {
    let message = 'Export failed — try again';
    try {
      message = JSON.parse(await err.response.data.text()).message || message;
    } catch (decodeErr) {
      // Non-JSON or network-level failure — keep the generic message.
    }
    toast.error(message);
  }
};

const QueueTab = () => {
  const [reports, setReports] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [employeeFilter, setEmployeeFilter] = useState('all');
  const [typedSearch, setTypedSearch] = useState(EMPTY_SEARCH);
  const [appliedSearch, setAppliedSearch] = useState(EMPTY_SEARCH);
  const [owners, setOwners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();

  // Exports the CURRENT filters — the document mirrors what the tab shows.
  const handleExport = async () => {
    const params = new URLSearchParams();
    if (statusFilter !== 'all') params.set('status', statusFilter);
    if (employeeFilter !== 'all') params.set('employee_id', employeeFilter);
    appendSearchParams(params, appliedSearch);
    const qs = params.toString();
    setExporting(true);
    await downloadExport(
      `/expenses/exports/queue${qs ? `?${qs}` : ''}`,
      `expense-queue-${new Date().toISOString().slice(0, 10)}.xlsx`
    );
    setExporting(false);
  };

  const fetchReports = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(offset) });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (employeeFilter !== 'all') params.set('employee_id', employeeFilter);
      appendSearchParams(params, appliedSearch);
      const response = await api.get(`/expenses?${params.toString()}`);
      setReports(Array.isArray(response.data?.reports) ? response.data.reports : []);
      setTotal(Number(response.data?.total) || 0);
    } catch (err) {
      // The builder's one-sentence 400s (e.g. "amount_min cannot exceed
      // amount_max") are user-facing — surface them over the generic line.
      toast.error(err.response?.data?.message || 'Failed to load expense reports');
    } finally {
      setLoading(false);
    }
  }, [offset, statusFilter, employeeFilter, appliedSearch]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // P2 debounce: typed -> applied after 400 ms of quiet; cleared on every
  // keystroke and on unmount. Unchanged (trimmed) values never re-apply.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch((prev) => (sameSearch(prev, typedSearch) ? prev : typedSearch));
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [typedSearch]);
  // Any applied-search change is a filter change -> back to page 1.
  useEffect(() => {
    setOffset(0);
  }, [appliedSearch]);
  const applyNow = () => setAppliedSearch((prev) => (sameSearch(prev, typedSearch) ? prev : typedSearch));
  const onSearchKeyDown = (e) => { if (e.key === 'Enter') applyNow(); };
  const setSearchField = (k) => (e) => setTypedSearch((prev) => ({ ...prev, [k]: e.target.value }));

  useEffect(() => {
    const fetchOwners = async () => {
      try {
        const response = await api.get('/expenses/owners');
        setOwners(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        toast.error('Failed to load employee filter');
      }
    };
    fetchOwners();
  }, []);

  const hasFilters = statusFilter !== 'all' || employeeFilter !== 'all'
    || SEARCH_KEYS.some((k) => appliedSearch[k].trim() !== '');
  const from = total === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE_SIZE, total);

  const clearFilters = () => {
    setStatusFilter('all');
    setEmployeeFilter('all');
    setTypedSearch(EMPTY_SEARCH);
    setAppliedSearch(EMPTY_SEARCH);
    setOffset(0);
  };

  return (
    <>
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        {STATUS_FILTERS.map((s) => (
          <button
            key={s}
            className={statusFilter === s ? 'btn-primary btn-small' : 'btn-small'}
            onClick={() => { setStatusFilter(s); setOffset(0); }}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </button>
        ))}
        <input
          type="text"
          value={typedSearch.title}
          onChange={setSearchField('title')}
          onKeyDown={onSearchKeyDown}
          maxLength={100}
          placeholder="Title contains"
          aria-label="Title contains"
          style={{ width: '160px' }}
        />
        <input
          type="text"
          value={typedSearch.reference}
          onChange={setSearchField('reference')}
          onKeyDown={onSearchKeyDown}
          maxLength={50}
          placeholder="Check #"
          aria-label="Check number"
          style={{ width: '100px' }}
        />
        <input
          type="text"
          inputMode="decimal"
          value={typedSearch.amount_min}
          onChange={setSearchField('amount_min')}
          onKeyDown={onSearchKeyDown}
          placeholder="Min $"
          aria-label="Minimum total"
          style={{ width: '80px' }}
        />
        <input
          type="text"
          inputMode="decimal"
          value={typedSearch.amount_max}
          onChange={setSearchField('amount_max')}
          onKeyDown={onSearchKeyDown}
          placeholder="Max $"
          aria-label="Maximum total"
          style={{ width: '80px' }}
        />
        <select
          value={employeeFilter}
          onChange={(e) => { setEmployeeFilter(e.target.value); setOffset(0); }}
          style={{ marginLeft: 'auto' }}
        >
          <option value="all">All employees</option>
          {owners.map((o) => (
            <option key={o.employee_id} value={o.employee_id}>{o.name}</option>
          ))}
        </select>
        {hasPermission('expenses', 'export') && (
          <button className="btn-small" disabled={exporting} onClick={handleExport}>
            {exporting ? 'Exporting…' : 'Export XLSX'}
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading expense reports...</div>
      ) : reports.length === 0 ? (
        <div className="loading">
          {hasFilters ? (
            <>
              <p>No reports match these filters.</p>
              <button className="btn-small" onClick={clearFilters}>Clear filters</button>
            </>
          ) : (
            <p>No expense reports yet.</p>
          )}
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Title</th>
                  <th>Status</th>
                  <th>Total</th>
                  <th>Created</th>
                  <th>Check #</th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr
                    key={report.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/expenses/${report.id}`)}
                  >
                    <td>{report.employee_name || '-'}</td>
                    <td>{report.title}</td>
                    <td>
                      <StatusBadge status={report.status} />
                      {report.self_approved && (
                        <span style={{
                          marginLeft: '6px', padding: '2px 6px', borderRadius: '8px',
                          fontSize: '10px', fontWeight: 700,
                          background: 'rgba(124, 58, 237, 0.15)', color: '#A78BFA'
                        }}>
                          SELF
                        </span>
                      )}
                    </td>
                    <td>{formatAmount(report.total_amount)}</td>
                    <td>{new Date(report.created_at).toLocaleDateString()}</td>
                    {/* P2 (P1 ruling 9): bare canonical value, blank when null, every row */}
                    <td>{report.payment_reference || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px', alignItems: 'center', justifyContent: 'flex-end' }}>
            <span style={{ color: 'var(--text-secondary)' }}>{from}–{to} of {total}</span>
            <button
              className="btn-small"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
            >
              ← Prev
            </button>
            <button
              className="btn-small"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset(offset + PAGE_SIZE)}
            >
              Next →
            </button>
          </div>
        </>
      )}
    </>
  );
};

const SUMMARY_STATUSES = ['submitted', 'approved', 'paid'];

const nameSlug = (name) => String(name || '').toLowerCase()
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40) || 'employee';

// A4b: per-employee statement picker — employee + month → statement PDF.
// Employee list is the Limits tab's source (GET /employees?status=active).
const StatementModal = ({ defaultMonth, onClose }) => {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [month, setMonth] = useState(defaultMonth);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get('/employees?status=active');
        setEmployees(Array.isArray(response.data?.employees) ? response.data.employees : []);
      } catch (err) {
        toast.error('Failed to load employees');
      }
    };
    fetchEmployees();
  }, []);

  const handleDownload = async () => {
    if (!employeeId || !month) return;
    const emp = employees.find((e) => e.id === employeeId);
    setDownloading(true);
    await downloadExport(
      `/expenses/exports/statement?employee_id=${employeeId}&month=${month}`,
      `expense-statement-${nameSlug(emp?.name)}-${month}.pdf`
    );
    setDownloading(false);
  };

  return (
    <div className="modal-overlay" onClick={() => !downloading && onClose()}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Employee Statement</h2>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
          One employee, one month — items, receipts, and the total against their
          monthly limit. An empty month produces a definitive empty statement.
        </p>
        <div className="form-group">
          <label>Employee *</label>
          <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="" disabled>Select an employee</option>
            {employees.map((e) => (
              <option key={e.id} value={e.id}>{e.name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Month *</label>
          <input
            type="month"
            value={month}
            onChange={(e) => { if (e.target.value) setMonth(e.target.value); }}
          />
        </div>
        <div className="modal-actions">
          <button type="button" onClick={onClose}>Close</button>
          <button
            type="button"
            className="btn-primary"
            disabled={downloading || !employeeId || !month}
            onClick={handleDownload}
          >
            {downloading ? 'Exporting…' : 'Download PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

const SummaryTab = () => {
  const [month, setMonth] = useState(localMonth());
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingFormat, setExportingFormat] = useState(null); // 'pdf' | 'xlsx' | null
  const [rangeFrom, setRangeFrom] = useState(localMonth());
  const [rangeTo, setRangeTo] = useState(localMonth());
  const [exportingRange, setExportingRange] = useState(null); // 'pdf' | 'xlsx' | null
  const [showStatement, setShowStatement] = useState(false);
  const { hasPermission } = usePermissions();

  // Exports the tab's current explicit month (D6 — never the server default).
  const handleExport = async (format) => {
    setExportingFormat(format);
    await downloadExport(
      `/expenses/exports/summary?month=${month}&format=${format}`,
      `expense-summary-${month}.${format}`
    );
    setExportingFormat(null);
  };

  // A4b range export — from/to month-list semantics; the server re-validates
  // (from<=to, 24-month cap), this disable is UX only.
  const rangeValid = rangeFrom && rangeTo && rangeFrom <= rangeTo;
  const handleRangeExport = async (format) => {
    if (!rangeValid) return;
    setExportingRange(format);
    await downloadExport(
      `/expenses/exports/range?from=${rangeFrom}&to=${rangeTo}&format=${format}`,
      `expense-range-${rangeFrom}-to-${rangeTo}.${format}`
    );
    setExportingRange(null);
  };

  // A4c: QuickBooks Online CSV — same range params, paid-only server-side.
  const handleQboExport = async () => {
    if (!rangeValid) return;
    setExportingRange('qbo');
    await downloadExport(
      `/expenses/exports/qbo?from=${rangeFrom}&to=${rangeTo}`,
      `expense-qbo-${rangeFrom}-to-${rangeTo}.csv`
    );
    setExportingRange(null);
  };

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        setLoading(true);
        const response = await api.get(`/expenses/summary?month=${month}`);
        setSummary(response.data || null);
      } catch (err) {
        toast.error('Failed to load expense summary');
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    if (month) fetchSummary();
  }, [month]);

  // Pivot by_employee (flat employee×status rows) into one row per employee.
  // Number() exactly once, here at the boundary — NUMERICs arrive as strings.
  const byEmployee = {};
  for (const row of summary?.by_employee || []) {
    const p = byEmployee[row.employee_id]
      || (byEmployee[row.employee_id] = { name: row.employee_name, cells: {}, total: 0 });
    const amount = Number(row.total);
    p.cells[row.status] = { amount, count: Number(row.report_count) };
    p.total += amount;
  }
  const employeeRows = Object.values(byEmployee).sort((a, b) => a.name.localeCompare(b.name));
  const grand = { cells: {}, total: 0 };
  for (const s of SUMMARY_STATUSES) {
    grand.cells[s] = employeeRows.reduce((sum, r) => sum + (r.cells[s]?.amount || 0), 0);
  }
  grand.total = employeeRows.reduce((sum, r) => sum + r.total, 0);

  const categoryRows = (summary?.by_category || []).map((c) => ({
    category: c.category,
    items: Number(c.item_count),
    total: Number(c.total)
  }));
  const categoryTotals = categoryRows.reduce(
    (acc, c) => ({ items: acc.items + c.items, total: acc.total + c.total }),
    { items: 0, total: 0 }
  );

  const isEmpty = employeeRows.length === 0 && categoryRows.length === 0;

  return (
    <>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
        <label htmlFor="summary-month"><strong>Month:</strong></label>
        <input
          id="summary-month"
          type="month"
          value={month}
          onChange={(e) => { if (e.target.value) setMonth(e.target.value); }}
        />
        {hasPermission('expenses', 'export') && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
            <button
              className="btn-small"
              disabled={exportingFormat !== null}
              onClick={() => handleExport('pdf')}
            >
              {exportingFormat === 'pdf' ? 'Exporting…' : 'Export PDF'}
            </button>
            <button
              className="btn-small"
              disabled={exportingFormat !== null}
              onClick={() => handleExport('xlsx')}
            >
              {exportingFormat === 'xlsx' ? 'Exporting…' : 'Export XLSX'}
            </button>
          </div>
        )}
      </div>

      {hasPermission('expenses', 'export') && (
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
          <label htmlFor="range-from"><strong>Range:</strong></label>
          <input
            id="range-from"
            type="month"
            value={rangeFrom}
            onChange={(e) => { if (e.target.value) setRangeFrom(e.target.value); }}
          />
          <span style={{ color: 'var(--text-secondary)' }}>to</span>
          <input
            type="month"
            value={rangeTo}
            onChange={(e) => { if (e.target.value) setRangeTo(e.target.value); }}
          />
          <button
            className="btn-small"
            disabled={exportingRange !== null || !rangeValid}
            title={rangeValid ? undefined : 'From must not be after To'}
            onClick={() => handleRangeExport('pdf')}
          >
            {exportingRange === 'pdf' ? 'Exporting…' : 'Range PDF'}
          </button>
          <button
            className="btn-small"
            disabled={exportingRange !== null || !rangeValid}
            title={rangeValid ? undefined : 'From must not be after To'}
            onClick={() => handleRangeExport('xlsx')}
          >
            {exportingRange === 'xlsx' ? 'Exporting…' : 'Range XLSX'}
          </button>
          <button
            className="btn-small"
            disabled={exportingRange !== null || !rangeValid}
            title={rangeValid ? undefined : 'From must not be after To'}
            onClick={handleQboExport}
          >
            {exportingRange === 'qbo' ? 'Exporting…' : 'QBO CSV'}
          </button>
          <button
            className="btn-small"
            style={{ marginLeft: 'auto' }}
            onClick={() => setShowStatement(true)}
          >
            Employee Statement…
          </button>
        </div>
      )}

      {hasPermission('expenses', 'export') && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '12px', marginTop: '-8px', marginBottom: '16px' }}>
          QBO CSV: paid expenses only, money out in the Debit column — import
          into QuickBooks Online as a 4-column bank CSV.
        </p>
      )}

      {showStatement && (
        <StatementModal defaultMonth={month} onClose={() => setShowStatement(false)} />
      )}

      {loading ? (
        <div className="loading">Loading summary...</div>
      ) : isEmpty ? (
        <div className="loading"><p>No expense activity in {month}.</p></div>
      ) : (
        <>
          <h2 style={{ margin: '0 0 8px' }}>By employee</h2>
          <div className="table-container" style={{ marginBottom: '24px' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Submitted</th>
                  <th>Approved</th>
                  <th>Paid</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {employeeRows.map((r) => (
                  <tr key={r.name}>
                    <td>{r.name}</td>
                    {SUMMARY_STATUSES.map((s) => (
                      <td key={s}>
                        {r.cells[s] ? `${formatAmount(r.cells[s].amount)} (${r.cells[s].count})` : ''}
                      </td>
                    ))}
                    <td><strong>{formatAmount(r.total)}</strong></td>
                  </tr>
                ))}
                <tr>
                  <td><strong>Total</strong></td>
                  {SUMMARY_STATUSES.map((s) => (
                    <td key={s}><strong>{grand.cells[s] ? formatAmount(grand.cells[s]) : ''}</strong></td>
                  ))}
                  <td><strong>{formatAmount(grand.total)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h2 style={{ margin: '0 0 8px' }}>By category</h2>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Items</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {categoryRows.map((c) => (
                  <tr key={c.category}>
                    <td>{c.category}</td>
                    <td>{c.items}</td>
                    <td>{formatAmount(c.total)}</td>
                  </tr>
                ))}
                <tr>
                  <td><strong>Total</strong></td>
                  <td><strong>{categoryTotals.items}</strong></td>
                  <td><strong>{formatAmount(categoryTotals.total)}</strong></td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );
};

const LimitsTab = () => {
  const [employees, setEmployees] = useState([]);
  const [limits, setLimits] = useState([]);
  const [defaultLimit, setDefaultLimit] = useState(500);
  const [loading, setLoading] = useState(true);
  const [editTarget, setEditTarget] = useState(null); // { id, name, current }
  const [amountInput, setAmountInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null); // { id, name }
  const [removing, setRemoving] = useState(false);
  const [editingDefault, setEditingDefault] = useState(false);
  const [defaultInput, setDefaultInput] = useState('');
  const [savingDefault, setSavingDefault] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [emp, lim] = await Promise.all([
        api.get('/employees?status=active'),
        api.get('/expenses/limits')
      ]);
      setEmployees(Array.isArray(emp.data?.employees) ? emp.data.employees : []);
      setLimits(Array.isArray(lim.data?.limits) ? lim.data.limits : []);
      setDefaultLimit(Number(lim.data?.default_monthly_limit) || 500);
    } catch (err) {
      toast.error('Failed to load expense limits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const limitByEmployee = {};
  for (const l of limits) limitByEmployee[l.employee_id] = l;

  const amountValid = LIMIT_AMOUNT_RE.test(amountInput.trim());

  const handleSave = async () => {
    if (!editTarget || !amountValid) return;
    try {
      setSaving(true);
      await api.put(`/expenses/limits/${editTarget.id}`, { monthly_limit: amountInput.trim() });
      toast.success(`Limit set for ${editTarget.name}`);
      setEditTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to set limit');
    } finally {
      setSaving(false);
    }
  };

  // A3b: the org-wide default is client-editable (system_settings-backed).
  const defaultValid = LIMIT_AMOUNT_RE.test(defaultInput.trim());
  const handleSaveDefault = async () => {
    if (!defaultValid) return;
    try {
      setSavingDefault(true);
      await api.put('/expenses/limits/default', { monthly_limit: defaultInput.trim() });
      toast.success('Default monthly limit updated');
      setEditingDefault(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update default limit');
    } finally {
      setSavingDefault(false);
    }
  };

  const handleRemove = async () => {
    if (!removeTarget) return;
    try {
      setRemoving(true);
      await api.delete(`/expenses/limits/${removeTarget.id}`);
      toast.success(`${removeTarget.name} reverts to the default limit`);
      setRemoveTarget(null);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to remove limit');
      setRemoveTarget(null);
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
        Default monthly limit: {formatAmount(defaultLimit)} — applies to everyone without a custom limit.
        <button
          className="btn-small"
          style={{ marginLeft: '8px' }}
          onClick={() => { setDefaultInput(String(defaultLimit)); setEditingDefault(true); }}
        >
          Edit
        </button>
      </p>

      {loading ? (
        <div className="loading">Loading limits...</div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Monthly limit</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((emp) => {
                const custom = limitByEmployee[emp.id];
                return (
                  <tr key={emp.id}>
                    <td>{emp.name}</td>
                    <td>
                      {custom ? (
                        <strong>{formatAmount(custom.monthly_limit)}</strong>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)' }}>
                          {formatAmount(defaultLimit)} (default)
                        </span>
                      )}
                    </td>
                    <td>
                      <button
                        className="btn-small"
                        onClick={() => {
                          setAmountInput(custom ? String(custom.monthly_limit) : '');
                          setEditTarget({ id: emp.id, name: emp.name });
                        }}
                      >
                        {custom ? 'Edit' : 'Set limit'}
                      </button>
                      {custom && (
                        <button
                          className="btn-small btn-danger"
                          style={{ marginLeft: '8px' }}
                          onClick={() => setRemoveTarget({ id: emp.id, name: emp.name })}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editTarget && (
        <div className="modal-overlay" onClick={() => !saving && setEditTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Set monthly limit for {editTarget.name}</h2>
            <div className="form-group">
              <label>Monthly limit ($) *</label>
              <input
                type="text"
                inputMode="decimal"
                value={amountInput}
                onChange={(e) => setAmountInput(e.target.value)}
                placeholder="e.g. 250.00"
                autoFocus
              />
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              A limit of $0 flags every submission from this employee with a budget warning.
              Warnings are advisory — they never block submission.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setEditTarget(null)}>Cancel</button>
              <button
                type="button"
                className="btn-primary"
                disabled={saving || !amountValid}
                onClick={handleSave}
              >
                {saving ? 'Saving...' : 'Save Limit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingDefault && (
        <div className="modal-overlay" onClick={() => !savingDefault && setEditingDefault(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Edit Default Monthly Limit</h2>
            <div className="form-group">
              <label>Default monthly limit ($) *</label>
              <input
                type="text"
                inputMode="decimal"
                value={defaultInput}
                onChange={(e) => setDefaultInput(e.target.value)}
                placeholder="e.g. 500.00"
                autoFocus
              />
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
              A $0 default flags every submission from every employee without a
              custom limit. Warnings are advisory — they never block.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setEditingDefault(false)}>Cancel</button>
              <button
                type="button"
                className="btn-primary"
                disabled={savingDefault || !defaultValid}
                onClick={handleSaveDefault}
              >
                {savingDefault ? 'Saving...' : 'Save Default'}
              </button>
            </div>
          </div>
        </div>
      )}

      {removeTarget && (
        <div className="modal-overlay" onClick={() => setRemoveTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Remove Custom Limit</h2>
            <p style={{ marginBottom: '16px' }}>
              Remove {removeTarget.name}'s custom limit? They revert to the {formatAmount(defaultLimit)} default.
            </p>
            <div className="modal-actions">
              <button type="button" onClick={() => setRemoveTarget(null)}>Cancel</button>
              <button type="button" className="btn-primary btn-danger" disabled={removing} onClick={handleRemove}>
                {removing ? 'Removing...' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// E3: on-behalf-of report creation — the office builds reports for employees
// without PC access. Creation only; the draft lands in the standard
// ExpenseDetail composition flow (items/receipts/QR/submit), owned by the
// selected employee with created_by = the caller.
const CreateForEmployeeModal = ({ onClose }) => {
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [title, setTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await api.get('/employees?status=active');
        setEmployees(Array.isArray(response.data?.employees) ? response.data.employees : []);
      } catch (err) {
        toast.error('Failed to load employees');
      }
    };
    fetchEmployees();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!employeeId || !title.trim()) return;
    try {
      setCreating(true);
      const response = await api.post('/expenses', {
        title: title.trim(),
        employee_id: employeeId
      });
      toast.success('Draft created');
      navigate(`/expenses/${response.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create report');
      setCreating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={() => !creating && onClose()}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>New Report for Employee</h2>
        <form onSubmit={handleCreate}>
          <div className="form-group">
            <label>Employee *</label>
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="" disabled>Select an employee</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. March fuel receipts"
              autoFocus
            />
          </div>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '16px' }}>
            You won't be able to approve this report — another decision-holder
            must decide it.
          </p>
          <div className="modal-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="btn-primary"
              disabled={creating || !employeeId || !title.trim()}
            >
              {creating ? 'Creating...' : 'Create Draft'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TABS = [
  { key: 'queue', label: 'Queue' },
  { key: 'summary', label: 'Summary' },
  { key: 'limits', label: 'Limits' }
];

const ExpensesAdmin = () => {
  const [activeTab, setActiveTab] = useState('queue');
  const [showCreateFor, setShowCreateFor] = useState(false);
  const { hasPermission, loading: permissionsLoading } = usePermissions();

  const canManage = hasPermission('expenses', 'manage');

  // While permissions resolve, show the loading state — never the fail-open flash.
  if (permissionsLoading) {
    return <div className="page-container"><div className="loading">Loading...</div></div>;
  }

  if (!canManage) {
    return (
      <div className="page-container">
        <div className="page-header"><h1>Expense Queue</h1></div>
        <div className="loading">You don't have access to the expense queue.</div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Expense Queue</h1>
        <button className="btn-primary" onClick={() => setShowCreateFor(true)}>
          + New Report for Employee
        </button>
      </div>

      {showCreateFor && (
        <CreateForEmployeeModal onClose={() => setShowCreateFor(false)} />
      )}

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            className={activeTab === t.key ? 'btn-primary btn-small' : 'btn-small'}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'queue' && <QueueTab />}
      {activeTab === 'summary' && <SummaryTab />}
      {activeTab === 'limits' && <LimitsTab />}
    </div>
  );
};

export default ExpensesAdmin;
